import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/axiosConfig';
import {
  requestNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  checkSubscriptionStatus,
  getNotificationPermissionStatus,
  getCurrentSubscription
} from '../utils/pushNotifications';

// VAPID public key - This should match your backend VAPID public key
// You can get this from your backend admin or environment variables
const VAPID_PUBLIC_KEY = "BEeQ2qpC7kQ3umDXARrl0hk13WUu_gBTNHSQPF-AGXzgeI0VAPJuv8LW_vayQ4p0QDwCVyrqQ0x_-TfZCu_EGew";

export const usePushNotification = () => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
  }, []);

  // Check initial subscription status
  useEffect(() => {
    if (isAuthenticated) {
      checkInitialStatus();
    }
  }, [isAuthenticated]);

  const checkInitialStatus = useCallback(async () => {
    const subscribed = await checkSubscriptionStatus();
    setIsSubscribed(subscribed);
    setPermissionStatus(getNotificationPermissionStatus());
  }, []);

  /**
   * Subscribe user to push notifications
   */
  const subscribe = useCallback(async () => {
    // Check authentication first
    if (!isAuthenticated) {
      toast.error('Please log in to enable notifications');
      return false;
    }

    if (!VAPID_PUBLIC_KEY) {
      toast.error('Push notifications not configured');
      console.error('VAPID_PUBLIC_KEY is not set. Please add VITE_VAPID_PUBLIC_KEY to your .env file');
      return false;
    }

    setIsLoading(true);
    try {
      // Request permission
      const granted = await requestNotificationPermission();
      
      if (!granted) {
        toast.error('Notification permission denied');
        setPermissionStatus('denied');
        return false;
      }

      setPermissionStatus('granted');

      // Subscribe to push notifications
      const subscription = await subscribeToPushNotifications(VAPID_PUBLIC_KEY);

      // Send subscription to backend
      const response = await api.post('/push/subscribe', {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: subscription.toJSON().keys.p256dh,
          auth: subscription.toJSON().keys.auth
        }
      });

      if (response.status === 201) {
        setIsSubscribed(true);
        toast.success('Successfully subscribed to notifications!');
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      
      if (error.response?.status === 409) {
        toast.error('Already subscribed to notifications');
        setIsSubscribed(true);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to subscribe to notifications');
      }
      
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Unsubscribe user from push notifications
   */
  const unsubscribe = useCallback(async () => {
    // Check authentication first
    if (!isAuthenticated) {
      toast.error('Please log in to manage notifications');
      return false;
    }

    setIsLoading(true);
    try {
      // Get current subscription
      const subscription = await getCurrentSubscription();
      
      if (subscription) {
        // Unsubscribe from browser
        await unsubscribeFromPushNotifications();
      }

      // Notify backend
      await api.delete('/push/unsubscribe');

      setIsSubscribed(false);
      toast.success('Successfully unsubscribed from notifications');
      return true;
    } catch (error) {
      console.error('Error unsubscribing from notifications:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to unsubscribe from notifications');
      }
      
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Toggle subscription status
   */
  const toggleSubscription = useCallback(async () => {
    if (isSubscribed) {
      return await unsubscribe();
    } else {
      return await subscribe();
    }
  }, [isSubscribed, subscribe, unsubscribe]);

  return {
    isSubscribed,
    isLoading,
    permissionStatus,
    isAuthenticated,
    subscribe,
    unsubscribe,
    toggleSubscription,
    checkStatus: checkInitialStatus
  };
};

